/**
 * Product taste selector: switches between related products (same product line, different tastes)
 * using Section Rendering API. Replaces <product-info> and updates URL.
 */
if (!customElements.get('product-taste-select')) {
  customElements.define(
    'product-taste-select',
    class ProductTasteSelect extends HTMLElement {
      constructor() {
        super();
        this.selectElement = this.querySelector('select');
        this.labelElement = this.querySelector('.js-select-label');
        this.trigger = this.querySelector('.js-taste-trigger');
        this.menu = this.querySelector('.js-taste-menu');
        this.optionButtons = this.querySelectorAll('.js-taste-option');
        this.products = [];
        this.sectionId = '';
        this.abortController = null;
        this.onDocumentClick = this.handleDocumentClick.bind(this);
      }

      connectedCallback() {
        try {
          const dataProducts = this.dataset.products;
          if (dataProducts) this.products = JSON.parse(dataProducts);
        } catch (e) {
          console.warn('product-taste-select: invalid data-products', e);
        }
        this.sectionId = this.dataset.sectionId || '';

        if (!this.selectElement || !this.labelElement) return;
        if (!this.products.length || !this.sectionId) return;

        this.optionButtons.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            const url = btn.dataset.url;
            if (!url) return;
            this.closeMenu();
            this.fetchAndReplaceProductInfo(url);
          });
        });

        if (this.trigger) {
          this.trigger.addEventListener('click', () => {
            this.toggleMenu();
          });
        }

        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        document.removeEventListener('click', this.onDocumentClick);
      }

      toggleMenu() {
        if (!this.menu || !this.trigger) return;
        const isOpen = !this.menu.classList.contains('hidden');
        if (isOpen) this.closeMenu();
        else this.openMenu();
      }

      openMenu() {
        if (!this.menu || !this.trigger) return;
        this.menu.classList.remove('hidden');
        this.trigger.setAttribute('aria-expanded', 'true');
      }

      closeMenu() {
        if (!this.menu || !this.trigger) return;
        this.menu.classList.add('hidden');
        this.trigger.setAttribute('aria-expanded', 'false');
      }

      handleDocumentClick(event) {
        if (!this.menu || this.menu.classList.contains('hidden')) return;
        if (this.contains(event.target)) return;
        this.closeMenu();
      }

      fetchAndReplaceProductInfo(productUrl) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const url = `${productUrl}${productUrl.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(this.sectionId)}`;

        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((htmlString) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlString, 'text/html');
            const newProductInfo = doc.querySelector('product-info');
            const currentProductInfo = this.closest('product-info');

            if (!newProductInfo || !currentProductInfo) {
              console.warn('product-taste-select: product-info not found in response or DOM');
              return;
            }

            currentProductInfo.replaceWith(newProductInfo);
            window.history.replaceState({}, '', productUrl);
          })
          .catch((err) => {
            if (err.name === 'AbortError') return;
            console.error('product-taste-select: fetch/replace failed', err);
          })
          .finally(() => {
            this.removeAttribute('loading');
          });
      }
    }
  );
}